/**
 * Per-table response adapters for the Nostos v3 external API.
 *
 * The v3 API returns each table column as a raw warehouse JSON string (snake_case
 * keys, array-of-struct wrapping, and in some tables string-typed numerics), while
 * components consume the `externallyAccessibleNostos.v1` element shape
 * (`{ id, content }` with camelCase, typed values). Each adapter converts a v3
 * `entries` payload into that element shape so component behavior is unchanged.
 */
import type { NostosExternalEntry } from 'bundles/nostos-external/queries';

export type NostosElement<TContent> = {
  id: string;
  content: TContent;
};

const getColumnValue = (entry: NostosExternalEntry, column: string): string | undefined =>
  entry.data.find((columnData) => columnData.column === column)?.value;

const parseJsonValue = (value: string | undefined): unknown => {
  if (!value) {
    return undefined;
  }
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const asFiniteNumber = (value: unknown): number | undefined => {
  const asNumber = typeof value === 'string' ? Number(value) : value;
  return typeof asNumber === 'number' && Number.isFinite(asNumber) ? asNumber : undefined;
};

const snakeToCamelKey = (key: string): string => key.replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase());

const snakeToCamelKeys = (row: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  Object.keys(row).forEach((key) => {
    result[snakeToCamelKey(key)] = row[key];
  });
  return result;
};

const adaptEntries = <TContent>(
  entries: NostosExternalEntry[] | undefined,
  toContent: (entry: NostosExternalEntry) => TContent | undefined
): Array<NostosElement<TContent>> | undefined => {
  if (!entries) {
    return undefined;
  }
  const elements: Array<NostosElement<TContent>> = [];
  entries.forEach((entry) => {
    const content = toContent(entry);
    if (content !== undefined) {
      elements.push({ id: entry.key, content });
    }
  });
  return elements;
};

export type EstimatedReadTimeContent = {
  estimatedreadingtime?: number;
  estimatedreadingtimelabel?: string;
};

/**
 * Table `articles_estimated_read_time` (id 120): column `content` is a
 * single-element array with numbers serialized as strings, e.g.
 * `[{"estimatedreadingtime":"6.615","estimatedreadingtimelabel":"7 min read",...}]`.
 */
export const adaptArticlesEstimatedReadTime = (
  entries: NostosExternalEntry[] | undefined
): Array<NostosElement<EstimatedReadTimeContent>> | undefined =>
  adaptEntries(entries, (entry) => {
    const parsed = parseJsonValue(getColumnValue(entry, 'content'));
    const row = Array.isArray(parsed) ? (parsed[0] as Record<string, unknown> | undefined) : undefined;
    if (!row) {
      return undefined;
    }
    return {
      estimatedreadingtime: asFiniteNumber(row.estimatedreadingtime),
      estimatedreadingtimelabel:
        typeof row.estimatedreadingtimelabel === 'string' ? row.estimatedreadingtimelabel : undefined,
    };
  });

export type MegamenuLoadMenuContent<TRow> = {
  load_menu: TRow[];
};

/**
 * Table `megamenu_nostos_job` (id 66): column `load_menu` matches the element
 * content shape after JSON parsing (lowercase keys on both sides).
 */
export const adaptMegamenuNostosJob = <TRow>(
  entries: NostosExternalEntry[] | undefined
): Array<NostosElement<MegamenuLoadMenuContent<TRow>>> | undefined =>
  adaptEntries(entries, (entry) => {
    const parsed = parseJsonValue(getColumnValue(entry, 'load_menu'));
    return Array.isArray(parsed) ? { load_menu: parsed as TRow[] } : undefined;
  });

export type MegamenuSkillDataContent = {
  skill_data: Array<{
    listIdentifier: string;
    rank: number;
    url: string;
  }>;
};

/**
 * Table `megamenu_popular_terms_by_domain` (id 47): column `skill_data` rows use
 * snake_case `list_identifier` where components expect camelCase `listIdentifier`.
 */
export const adaptMegamenuPopularTermsByDomain = (
  entries: NostosExternalEntry[] | undefined
): Array<NostosElement<MegamenuSkillDataContent>> | undefined =>
  adaptEntries(entries, (entry) => {
    const parsed = parseJsonValue(getColumnValue(entry, 'skill_data'));
    if (!Array.isArray(parsed)) {
      return undefined;
    }
    return {
      skill_data: (parsed as Array<Record<string, unknown>>).map((row) => ({
        listIdentifier: String(row.list_identifier ?? ''),
        rank: asFiniteNumber(row.rank) ?? 0,
        url: String(row.url ?? ''),
      })),
    };
  });

export type PortfolioProjectsContent = {
  projects: Array<Record<string, unknown>>;
};

/**
 * Tables `portfolio_projects` (id 9) and `portfolio_projects_by_slug` (id 39):
 * column `projects` rows use snake_case keys (`course_slug`, `business_challenge`,
 * `is_active`, ...) where components expect camelCase.
 */
export const adaptPortfolioProjects = (
  entries: NostosExternalEntry[] | undefined
): Array<NostosElement<PortfolioProjectsContent>> | undefined =>
  adaptEntries(entries, (entry) => {
    const parsed = parseJsonValue(getColumnValue(entry, 'projects'));
    if (!Array.isArray(parsed)) {
      return undefined;
    }
    return { projects: (parsed as Array<Record<string, unknown>>).map(snakeToCamelKeys) };
  });

export type HasLabAssignmentsContent = {
  hasLabAssignments?: boolean;
};

/**
 * Table `enterprise_products_with_labs` (id 68): column `product_ids` holds
 * `{"has_lab_assignments": <bool>}` where components expect a flat
 * `{ hasLabAssignments }` content object.
 */
export const adaptEnterpriseProductsWithLabs = (
  entries: NostosExternalEntry[] | undefined
): Array<NostosElement<HasLabAssignmentsContent>> | undefined =>
  adaptEntries(entries, (entry) => {
    const parsed = parseJsonValue(getColumnValue(entry, 'product_ids')) as Record<string, unknown> | undefined;
    if (!parsed || typeof parsed !== 'object' || !('has_lab_assignments' in parsed)) {
      return undefined;
    }
    return { hasLabAssignments: Boolean(parsed.has_lab_assignments) };
  });

export type ProductMetricsContent = {
  totalEnrollmentCount?: number;
  productPageViewsInLastMonthCount?: number;
};

/**
 * Table `product_metrics` (id 112): column `product_metrics` holds a nested
 * snake_case object where components expect the two metrics as flat camelCase
 * content properties.
 */
export const adaptProductMetrics = (
  entries: NostosExternalEntry[] | undefined
): Array<NostosElement<ProductMetricsContent>> | undefined =>
  adaptEntries(entries, (entry) => {
    const parsed = parseJsonValue(getColumnValue(entry, 'product_metrics')) as Record<string, unknown> | undefined;
    if (!parsed || typeof parsed !== 'object') {
      return undefined;
    }
    return {
      totalEnrollmentCount: asFiniteNumber(parsed.total_enrollment_count),
      productPageViewsInLastMonthCount: asFiniteNumber(parsed.product_page_views_in_last_month_count),
    };
  });

export type MixAndMatchS12nRankingContent = {
  enrollments: number;
};

/**
 * Table `mix_and_match_s12n_ranking`: keyed by s12n slug, column
 * `s12n_enrollments_trailing_6_mo` is the trailing six-month enrollment count.
 */
export const adaptMixAndMatchS12nRanking = (
  entries: NostosExternalEntry[] | undefined
): Array<NostosElement<MixAndMatchS12nRankingContent>> | undefined =>
  adaptEntries(entries, (entry) => {
    const enrollments = asFiniteNumber(parseJsonValue(getColumnValue(entry, 's12n_enrollments_trailing_6_mo')));
    return enrollments === undefined ? undefined : { enrollments };
  });

export type SearchQueryToEntityContent = {
  matchedEntity?: string;
  entityType?: string;
  intent?: string;
};

/**
 * Table `prod_search_queries_to_entities` (id 118): column `content` is a
 * single-element array of an already-camelCase object; components expect the
 * unwrapped object.
 */
export const adaptProdSearchQueriesToEntities = (
  entries: NostosExternalEntry[] | undefined
): Array<NostosElement<SearchQueryToEntityContent>> | undefined =>
  adaptEntries(entries, (entry) => {
    const parsed = parseJsonValue(getColumnValue(entry, 'content'));
    const row = Array.isArray(parsed) ? (parsed[0] as Record<string, unknown> | undefined) : undefined;
    if (!row) {
      return undefined;
    }
    return {
      matchedEntity: typeof row.matchedEntity === 'string' ? row.matchedEntity : undefined,
      entityType: typeof row.entityType === 'string' ? row.entityType : undefined,
      intent: typeof row.intent === 'string' ? row.intent : undefined,
    };
  });

export type RecentPathwayCompleterContent = {
  phoenixSpecializationId?: string;
};

/**
 * Table `recent_pathway_completers` (id 144): column `recent_pathway_completers`
 * holds `{"phoenix_specialization_id": "..."}` where components expect camelCase
 * `phoenixSpecializationId`.
 */
export const adaptRecentPathwayCompleters = (
  entries: NostosExternalEntry[] | undefined
): Array<NostosElement<RecentPathwayCompleterContent>> | undefined =>
  adaptEntries(entries, (entry) => {
    const parsed = parseJsonValue(getColumnValue(entry, 'recent_pathway_completers')) as
      Record<string, unknown> | undefined;
    if (!parsed || typeof parsed !== 'object') {
      return undefined;
    }
    return {
      phoenixSpecializationId:
        typeof parsed.phoenix_specialization_id === 'string' ? parsed.phoenix_specialization_id : undefined,
    };
  });

export type RelevantSkillsContent = {
  /** JSON-encoded string array of skill slugs; consumers `JSON.parse` this value. */
  skills?: string;
};

/**
 * Table `browse_relevant_skills_by_domain` (id 77): column `group_skills_by_key`
 * holds `{"skills": "[\"...\"]"}` — the inner value stays a JSON string, matching
 * the content shape consumers already parse.
 */
export const adaptBrowseRelevantSkillsByDomain = (
  entries: NostosExternalEntry[] | undefined
): Array<NostosElement<RelevantSkillsContent>> | undefined =>
  adaptEntries(entries, (entry) => {
    const parsed = parseJsonValue(getColumnValue(entry, 'group_skills_by_key')) as Record<string, unknown> | undefined;
    if (!parsed || typeof parsed !== 'object') {
      return undefined;
    }
    return { skills: typeof parsed.skills === 'string' ? parsed.skills : undefined };
  });
